import { memo, useState } from 'react';
import { formatDisplayDate } from '../../../utils/formatUtils';
import { formatCurrency } from '@/modules/shared/utils/formatCurrency';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, ExternalLink, MessageSquare, Phone, Upload, History } from 'lucide-react';
import { useDensityContext } from '../../../contexts/DensityContext';
import type { DynamicEntityData } from '../../../types/dynamicEntityView';
import type {
  PatientSummaryDto,
  Patient360CaseDto,
  Patient360CycleTimelineItemDto,
  Patient360FinancialDto,
  Patient360SchemeBreakdownDto,
} from '../../../types/patient360';
import type { Patient360CardVisibilityDto } from '../../../types/patient360CardVisibility';
import { Button } from '@/modules/shared/components/ui/button';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { useCycleWorkflowBehavior } from '../../../hooks/useCycleWorkflowBehavior';

interface OverviewSummary {
  activeCases: number;
  completedCases: number;
  totalCycles: number;
  completedCycles: number;
  totalAssistance: number;
  utilizedAmount?: number;
  remainingEligibility: number;
  lastInteraction: string | null;
  schemeBreakdown: Patient360SchemeBreakdownDto[];
}

interface OverviewTabProps {
  programId?: string;
  patient: PatientSummaryDto;
  cases?: Patient360CaseDto[];
  summary?: OverviewSummary;
  financial?: Patient360FinancialDto;
  cycleTimeline?: Patient360CycleTimelineItemDto[];
  patientEntity?: DynamicEntityData;
  hasLowBalance?: boolean;
  onViewCase?: (caseId: string) => void;
  onTakeAction?: () => void;
  hideMonetaryTrackingFields?: boolean;
  cardVisibility?: Patient360CardVisibilityDto;
  onCreateEnrollmentCase?: () => void;
  onViewCases?: () => void;
  onViewCycles?: () => void;
  onUploadDocument?: () => void;
  onCreateCallLog?: () => void;
  onSendMessage?: () => void;
  onViewTimeline?: () => void;
  canCreateEnrollmentCase?: boolean;
  canViewCases?: boolean;
  canUploadDocument?: boolean;
  canCreateCallLog?: boolean;
  canSendMessage?: boolean;
  canViewTimeline?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

function CollapsibleSection({ title, defaultOpen = true, badge, actions, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { isCompact } = useDensityContext();
  return (
    <div className="p360-card-elevated">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full flex items-center justify-between text-[12px] font-semibold text-foreground text-left',
          isCompact ? 'px-3 py-1.5' : 'px-3 py-2',
        )}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">{title}{badge}</span>
        <span className="flex items-center gap-2">
          {actions && <span onClick={(e) => e.stopPropagation()}>{actions}</span>}
          <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && <div className={cn('pt-0.5', isCompact ? 'px-3 pb-2 space-y-2' : 'px-3 pb-3 space-y-2')}>{children}</div>}
    </div>
  );
}

interface SectionCardProps {
  title?: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

function SectionCard({ title, badge, actions, children }: SectionCardProps) {
  const { isCompact } = useDensityContext();
  return (
    <div className="p360-card-elevated">
      {title && (
        <div className={cn(
          'flex items-center justify-between text-[12px] font-semibold text-foreground',
          isCompact ? 'px-3 py-1.5' : 'px-3 py-2',
        )}>
          <span className="flex items-center gap-2">{title}{badge}</span>
          {actions && <span>{actions}</span>}
        </div>
      )}
      <div className={cn(
        isCompact ? 'px-3 pb-2 space-y-2' : 'px-3 pb-3 space-y-2',
        title ? 'pt-0.5' : (isCompact ? 'py-2' : 'py-3'),
      )}>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div>
      <p className="text-[11px] text-muted-foreground mb-0.5">{label}</p>
      <p className={cn('text-[12px] font-medium', empty ? 'text-muted-foreground' : 'text-foreground')}>{empty ? '-' : value}</p>
    </div>
  );
}

function getCaseStatusStyle(status: string | undefined): { bg: string; text: string; label: string } {
  if (!status) return { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Unknown' };
  const normalized = status.toLowerCase();
  if (normalized.includes('active') || normalized.includes('approved')) return { bg: 'bg-success/15', text: 'text-success', label: 'Active' };
  if (normalized.includes('pending') || normalized.includes('review')) return { bg: 'bg-warning/15', text: 'text-warning-strong', label: 'Pending' };
  if (normalized.includes('reject')) return { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Rejected' };
  if (normalized.includes('close') || normalized.includes('complete')) return { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Closed' };
  return { bg: 'bg-muted', text: 'text-muted-foreground', label: status.replace(/_/g, ' ') };
}

function resolveFieldValue(formData: Record<string, unknown> | undefined, keys: string[]): string | null {
  if (!formData) {
    return null;
  }
  for (const key of keys) {
    const direct = formData[key];
    if (typeof direct === 'string' && direct.trim()) {
      return direct.trim();
    }
    const loweredKey = Object.keys(formData).find((k) => k.toLowerCase() === key.toLowerCase());
    if (!loweredKey) {
      continue;
    }
    const candidate = formData[loweredKey];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return null;
}

function formatDobWithAge(dateOfBirth: string | null, age: number | null): string {
  if (!dateOfBirth) {
    return '-';
  }
  const base = formatDisplayDate(dateOfBirth);
  if (age === null || age === undefined) {
    return base;
  }
  return `${base} (${age})`;
}

export const OverviewTab = memo(function OverviewTab({
  programId,
  patient,
  cases = [],
  summary,
  financial: _financial,
  patientEntity,
  hasLowBalance: _hasLowBalance = false,
  onViewCase,
  onTakeAction: _onTakeAction,
  hideMonetaryTrackingFields = false,
  cardVisibility,
  onCreateEnrollmentCase,
  onViewCases,
  onViewCycles,
  onUploadDocument,
  onCreateCallLog,
  onSendMessage,
  onViewTimeline,
  canCreateEnrollmentCase = false,
  canViewCases = true,
  canUploadDocument = true,
  canCreateCallLog = true,
  canSendMessage = true,
  canViewTimeline = true,
}: OverviewTabProps) {
  const activeCase = cases.find((c) => c.isActive) ?? null;
  const currentCycleNumber = Math.max(activeCase?.currentCycleNumber ?? 1, 1);
  const { behavior } = useCycleWorkflowBehavior({
    programId: programId ?? '',
    cycleNumber: currentCycleNumber,
    enabled: !!programId,
  });

  const totalCycleSlots = summary?.totalCycles != null ? Math.max(summary.totalCycles, 1) : null;
  const focCycleNumber = Math.max(behavior?.focEligibilityStartCycle ?? ((totalCycleSlots ?? 0) + 1), 1);
  const completedCycles = totalCycleSlots != null ? Math.min(summary?.completedCycles ?? 0, totalCycleSlots) : (summary?.completedCycles ?? 0);
  const caseStatusStyle = getCaseStatusStyle(activeCase?.currentWorkflowStepName ?? activeCase?.caseStatus);

  const formData = patient.formDataFields ?? {};
  const medicationName = resolveFieldValue(formData, ['medication', 'medicationName', 'drugName']);
  const contactValue = patient.contactNumber
    ?? resolveFieldValue(formData, ['contactNumber', 'phoneNumber', 'phone', 'mobile', 'mobileNumber', 'contact', 'primaryPhone', 'cellPhone', 'cellNumber', 'primaryContact']);

  // Patient 360 "Patient information" is config-driven: a field appears here only
  // when Form Builder marks it "Show in summary" AND the patient has a value for it.
  // Lookup/selector fields (form internal names vary per program) can store the
  // referenced Hospital/HCP's raw ID rather than a name. Match by value against
  // the patient's own known hospitalId/hcpId (not by field name, which isn't
  // stable across programs) and substitute the already-resolved names used
  // elsewhere on this page instead of the raw ID.
  const summaryFields = (patientEntity?.sections ?? [])
    .flatMap((section) => section.fields)
    .filter((field) =>
      field.displayConfig?.summaryVisible &&
      field.value !== null && field.value !== undefined && field.value !== '')
    .map((field) => {
      if (patient.hospitalId && field.value === patient.hospitalId) {
        return { ...field, displayValue: patient.hospitalName || field.displayValue };
      }
      if (patient.hcpId && field.value === patient.hcpId) {
        return { ...field, displayValue: patient.hcpName || field.displayValue };
      }
      return field;
    })
    .sort((a, b) => (a.displayConfig?.order ?? a.order ?? 0) - (b.displayConfig?.order ?? b.order ?? 0));

  const isCardVisible = (key: keyof Patient360CardVisibilityDto): boolean => {
    if (!cardVisibility) return true;
    const v = cardVisibility[key];
    return typeof v === 'boolean' ? v : true;
  };

  const quickActions = [
    {
      key: 'upload',
      label: 'Upload document',
      icon: Upload,
      onClick: onUploadDocument,
      enabled: canUploadDocument,
    },
    {
      key: 'call-log',
      label: 'Create call log',
      icon: Phone,
      onClick: onCreateCallLog,
      enabled: canCreateCallLog,
    },
    {
      key: 'message',
      label: 'Send message',
      icon: MessageSquare,
      onClick: onSendMessage,
      enabled: canSendMessage,
    },
    {
      key: 'timeline',
      label: 'View timeline',
      icon: History,
      onClick: onViewTimeline,
      enabled: canViewTimeline,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-3 space-y-2">
          {isCardVisible('caseSummary') && (
            <SectionCard
              title={activeCase ? 'Active enrollment case' : 'Enrollment case'}
              badge={activeCase ? <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium', caseStatusStyle.bg, caseStatusStyle.text)}>{caseStatusStyle.label}</span> : undefined}
              actions={activeCase ? (
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  {canViewCases && onViewCase && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onViewCase(activeCase.caseId)}>
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View Case
                    </Button>
                  )}
                  {canCreateEnrollmentCase && onCreateEnrollmentCase && (
                    <Button size="sm" className="h-7 text-[11px]" onClick={onCreateEnrollmentCase}>Add Enrollment Case</Button>
                  )}
                </div>
              ) : undefined}
            >
              {activeCase ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <InfoRow label="Case number" value={activeCase.caseNumber} />
                    <InfoRow label="Status" value={caseStatusStyle.label} />
                    <InfoRow label="Benefit plan" value={activeCase.benefitPlanName ?? '-'} />
                    <InfoRow label="Current treatment cycle" value={`Cycle ${Math.max(activeCase.currentCycleNumber ?? 1, 1)}`} />
                    <InfoRow label="Enrollment date" value={formatDisplayDate(activeCase.enrollmentDate)} />
                    <InfoRow label="Medication" value={medicationName ?? '-'} />
                    {!hideMonetaryTrackingFields && isCardVisible('financialTotalAssistance') && !!summary?.utilizedAmount && (
                      <InfoRow label="Assistance Provided" value={formatCurrency(summary.utilizedAmount)} />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[12px] text-muted-foreground">No active enrollment case. Create one to begin treatment cycles and assistance.</p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {canCreateEnrollmentCase && onCreateEnrollmentCase && (
                      <Button size="sm" className="h-7 text-[11px]" onClick={onCreateEnrollmentCase}>Create Enrollment Case</Button>
                    )}
                    {canViewCases && onViewCases && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onViewCases}>View All Cases</Button>
                    )}
                  </div>
                </>
              )}
            </SectionCard>
          )}

          {isCardVisible('currentCycle') && (
            <SectionCard
              title="Treatment cycles"
              actions={
                <span className="text-[11px] font-normal text-muted-foreground">
                  {totalCycleSlots != null ? `${completedCycles} of ${totalCycleSlots} completed` : 'Cycle count not configured'}
                </span>
              }
            >
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: totalCycleSlots ?? 0 }, (_, idx) => idx + 1).map((cycleNo) => {
                  const isCompleted = cycleNo <= completedCycles;
                  const isActive = activeCase ? cycleNo === Math.max(activeCase.currentCycleNumber ?? 1, 1) : false;
                  const label = isCompleted ? 'Completed' : isActive ? 'Current' : `Upcoming cycle ${cycleNo}`;
                  return (
                    <button
                      key={`cycle-${cycleNo}`}
                      type="button"
                      title={label}
                      onClick={onViewCycles}
                      className={cn(
                        'inline-flex items-center justify-center min-w-[22px] h-6 rounded-full border px-1.5 text-[11px] font-semibold transition-opacity',
                        onViewCycles ? 'cursor-pointer hover:opacity-75' : 'cursor-default',
                        isCompleted
                          ? 'bg-primary/15 border-primary/30 text-primary'
                          : isActive
                            ? 'bg-info/15 border-info/30 text-info ring-1 ring-info/40'
                            : 'bg-muted/40 border-border text-muted-foreground',
                      )}
                    >
                      {isCompleted ? <Check className="w-3 h-3" /> : cycleNo}
                    </button>
                  );
                })}
                <span
                  title={`Free-of-charge cycle unlocks at cycle ${focCycleNumber}`}
                  className="inline-flex items-center justify-center h-6 rounded-full border border-success/40 bg-success/15 px-2 text-[11px] font-semibold text-success"
                >
                  FOC
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">Free-of-charge unlocks at cycle {focCycleNumber} · current cycle highlighted with ring</p>
            </SectionCard>
          )}

          <SectionCard title="Quick actions">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {quickActions.map((action) => (
                <Button
                  key={action.key}
                  variant="outline"
                  size="sm"
                  className="h-8 justify-start text-[11px]"
                  onClick={action.onClick}
                  disabled={!action.enabled || !action.onClick}
                >
                  <AppIcon icon={action.icon} size="compact" className="mr-1.5" />
                  {action.label}
                </Button>
              ))}
            </div>
          </SectionCard>
        </div>

        <div className="lg:col-span-2 space-y-2">
          {isCardVisible('patientInformation') && (
            <SectionCard title="Patient information">
              {summaryFields.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {summaryFields.map((field) => (
                    <InfoRow key={field.fieldKey} label={field.label} value={field.displayValue} />
                  ))}
                </div>
              ) : (
                // Fallback for programs whose Patient Registration form has no fields
                // marked "Show in summary" yet, and no dynamic entity data loaded.
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InfoRow label="Date of birth" value={formatDobWithAge(patient.dateOfBirth, patient.age)} />
                  <InfoRow label="Gender" value={patient.gender ?? '-'} />
                  <InfoRow label="Email" value={patient.email ?? '-'} />
                  <InfoRow label="Diagnosis" value={patient.diagnosis ?? '-'} />
                  <InfoRow label="Contact" value={contactValue ?? '-'} />
                  <InfoRow label="Scheme" value={patient.schemeType ?? '-'} />
                  <InfoRow label="Enrolled" value={formatDisplayDate(patient.createdAt)} />
                </div>
              )}
            </SectionCard>
          )}

          {isCardVisible('enrollmentDetails') && (
            <CollapsibleSection title="Enrollment details" defaultOpen={true}>
              {/* Case number / cycle / benefit plan / medication already shown in the
                  enrollment case card above — only non-duplicate fields here. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <InfoRow label="Approval status" value={patient.approvalStatus ?? activeCase?.workflowState ?? '-'} />
                <InfoRow label="Hospital" value={patient.hospitalName || '-'} />
                <InfoRow label="HCP" value={patient.hcpName || '-'} />
              </div>
            </CollapsibleSection>
          )}
        </div>
      </div>
    </div>
  );
});
